/**
 * editing redux state modifiers
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


import {replaceNestedObjectProperty} from './utils';
import _ from 'lodash';



export function updateObjects(state, objects) {
    let newObjects = {...state.objects};
    objects.forEach(object => {
        const key = object.id;
        newObjects[key] = object;
    });
    return {
        ...state,
        objects: newObjects
    };
}

export function clearStack(state, objectId, object, objectInfo, additionalFields = {}) {
    const newEditStack = [{objectId: objectId, ...additionalFields}];
    return {
        ...state,
        objects: {[objectId]: object},
        originalObjects: {[objectId]: object},
        modified: {},
        objectInfo: {[objectId]: objectInfo},
        editStack: newEditStack
    };
}

export function pushObject(state, objectId, object, objectInfo, additionalFields = {}) {
    const newEditStack = [...state.editStack, {
        objectId: objectId,
        ...additionalFields
    }];
    return {
        ...state,
        objects: {...state.objects, [objectId]: object},
        originalObjects: {...state.originalObjects, [objectId]: object},
        objectInfo: {...state.objectInfo, [objectId]: objectInfo},
        editStack: newEditStack
    };
}

export function popObject(state) {
    const newEditStack = state.editStack.slice(0, -1);
    return {
        ...state,
        editStack: newEditStack
    }
}

/**
 * change the ID of the object shown for editing
 * (useful after a new ID was assigned by the server),
 * preserving additional fields
 * @param state         current state
 * @param objectId      new object id
 * @returns {Object}    new state
 * @author Jens Gruschel
 */
export function switchObjectId(state, objectId, accessLevel) {
    const topEntry = state.editStack.slice(-1)[0];
    const otherEntries = state.editStack.slice(0, -1);
    const newEditStack = [
        ...otherEntries, {
            ...topEntry,
            objectId: objectId,
        }
    ];
    const objectInfo = state.objectInfo[objectId] || {accessLevel: accessLevel};
    return {
        ...state,
        editStack: newEditStack,
        objectInfo: {
            ...state.objectInfo,
            [objectId]: objectInfo
        }
    };
}

/**
 * replace an object by a modified one (preserving tab index etc.)
 * @param state         current state
 * @param object        new object
 * @returns {Object}    new state
 * @author Jens Gruschel
 */
export function setObject(state, objectId, object, replaceOriginal) {

    if (replaceOriginal) {
        return {
            ...state,
            objects: {
                ...state.objects,
                [objectId]: object
            },
            originalObjects: {
                ...state.originalObjects,
                [objectId]: object
            },
            modified: {
                ...state.modified,
                [objectId]: false
            }
        };
    }
    else {
        const originalObject = state.originalObjects[objectId] || {};
        return {
            ...state,
            objects: {
                ...state.objects,
                [objectId]: object
            },
            modified: {
                ...state.modified,
                [objectId]: !_.isEqual(object, originalObject)
            }
        };
    }
}

/**
 * modify a single property of an object
 * @param state         current state
 * @param objectId      object identifier
 * @param fieldPath       key list as path to the property
 * @param value         new value of the property
 * @returns {Object}    new state
 * @author Jens Gruschel
 */
export function setObjectValue(state, objectId, fieldPath, value) {

    const originalObject = state.originalObjects[objectId] || {};
    const oldObject = state.objects[objectId] || {};
    const newObject = replaceNestedObjectProperty(oldObject, fieldPath, value);

    // special handling for media actions
    // TODO: handle elsewhere?
    if (newObject.type === 'mediaaction' && _.isEqual(fieldPath, ['fields', 'medialist']) && value) {
        if (newObject.fields.loop) {
            newObject.fields.duration = 0;
        }
        else {
            let seconds = value.reduce((sum, itemId) => {
                const mediaItem = state.objects[itemId];
                const duration = (mediaItem && mediaItem.mediainfo && mediaItem.mediainfo.duration) || 0.0;
                return sum + duration;
            }, 0.0);
            newObject.fields.duration = seconds.toFixed(2);
            newObject.fields.duration__suffix = 's';
        }
    }

    return {
        ...state,
        objects: {
            ...state.objects,
            [objectId]: newObject
        },
        modified: {
            ...state.modified,
            [objectId]: !_.isEqual(value, _.get(originalObject, fieldPath)) ? true :
                !_.isEqual(newObject, originalObject) // for performance reasons check given field first
        }
    };
}

/**
 * modify (nearly) all fields of an object (except ID etc.)
 * @param state         current state
 * @param objectId      object identifier
 * @param object        new object
 * @returns {Object}    new state
 * @author Jens Gruschel
 */
export function setWholeObject(state, objectId, object) {
    const originalObject = state.originalObjects[objectId] || {};
    const oldObject = state.objects[objectId] || {};
    const newObject = {
        ...object,
        id: oldObject.id,
        // type: oldObject.type,
        // "autocreated": oldObject["autocreated"],
        /* "timestamp-created": oldObject["timestamp-created"],
        "user-created": oldObject["user-created"],
        "timestamp-modified": oldObject["timestamp-modified"],
        "user-modified": oldObject["user-modified"],
        "timestamp-deleted": oldObject["timestamp-deleted"],
        "user-deleted": oldObject["user-deleted"] */
    };
    // instead of leaving fields with undefined values, remove them, which makes comparison of objects easier
    for (const field of ["autocreated", "timestamp-deleted", "user-deleted"]) {
        if (newObject[field] === undefined) delete newObject[field];
    }
    return {
        ...state,
        objects: {
            ...state.objects,
            [objectId]: newObject
        },
        modified: {
            ...state.modified,
            [objectId]: !_.isEqual(newObject, originalObject)
        }
    };
}

/**
 * change the tab index for the tab key of the topmost stack item
 * @param state         current state
 * @param tabKey        key identifying the tab set
 * @param index         new tab index
 * @returns {Object}    new state
 * @author Jens Gruschel
 */
export function setTabIndex(state, tabKey, index) {
    const topEntry = state.editStack.slice(-1)[0];
    const otherEntries = state.editStack.slice(0, -1);
    const newEditStack = [
        ...otherEntries, {
            ...topEntry,
            tabIndexes: {
                ...topEntry.tabIndexes,
                [tabKey]: index
            }
        }
    ];
    return {
        ...state,
        editStack: newEditStack
    };
}

export function setEditFlag(state, flagName, flagValue) {
    const topEntry = state.editStack.slice(-1)[0];
    const otherEntries = state.editStack.slice(0, -1);
    const newEditStack = [
        ...otherEntries, {
            ...topEntry,
            flags: {
                ...topEntry.flags,
                [flagName]: flagValue
            }
        }
    ];
    return {
        ...state,
        editStack: newEditStack
    };
}
